import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";

export default function Nav({ onAdminOpen }) {
  const { user, login, logout } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const loginRef = useRef(null);

  // Secret shortcut: Alt+Shift+A opens the login modal
  useEffect(() => {
    const handler = (e) => {
      if (e.altKey && e.shiftKey && e.code === "KeyA") {
        if (user) onAdminOpen();
        else setShowLogin(s => !s);
      }
      if (e.key === "Escape") setShowLogin(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [user, onAdminOpen]);

  // Close login dropdown when clicking outside
  useEffect(() => {
    if (!showLogin) return;
    const handler = (e) => {
      if (loginRef.current && !loginRef.current.contains(e.target)) setShowLogin(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showLogin]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await login(email, password);
      setShowLogin(false);
      onAdminOpen();
    } catch {
      setError("// Invalid credentials");
    }
  };

  return (
    <nav style={{ position: "sticky", top: 0, zIndex: 50, background: "#fff", borderBottom: "1px solid var(--ink)" }}>
      <div
        className="nav-inner"
        style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", alignItems: "center", padding: "18px 32px", gap: "32px", maxWidth: "1440px", margin: "0 auto" }}
      >
        {/* Logo */}
        <a href="#" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div className="logo-mark">
            <img src={`${process.env.PUBLIC_URL}/logo-he-enterprises.png`} alt="HE Enterprises logo" />
          </div>
          <div style={{ fontWeight: 600, letterSpacing: "-0.01em", fontSize: "15px", lineHeight: 1.2 }}>
            Hatharasinghe Enterprises
            <small style={{ display: "block", fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--muted)", fontWeight: 400, marginTop: "2px" }}>
              Est. 2004
            </small>
          </div>
        </a>

        {/* Nav links */}
        <ul className="nav-links-list" style={{ display: "flex", gap: "28px", justifyContent: "center", listStyle: "none" }}>
          <li><a href="#work" className="nav-link">Work</a></li>
          <li><a href="#about" className="nav-link">About</a></li>
          <li><a href="#services" className="nav-link">Services</a></li>
          <li><a href="#contact" className="nav-link">Contact</a></li>
        </ul>

        {/* CTA cluster */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px", position: "relative" }} ref={loginRef}>

          {/* Only show admin controls when already signed in */}
          {user && (
            <>
              <button
                onClick={onAdminOpen}
                style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--muted)", padding: "6px 10px", border: "1px solid var(--line)", background: "#fff", cursor: "pointer" }}
              >
                Admin ↗
              </button>
              <button
                onClick={logout}
                style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--muted)", padding: "6px 10px", border: "1px solid var(--line)", background: "#fff", cursor: "pointer" }}
              >
                Sign out
              </button>
            </>
          )}

          <a href="#contact" className="btn btn-sm">Request quote →</a>

          {/* Hidden login panel — triggered by Alt+Shift+A */}
          {showLogin && !user && (
            <div style={{ position: "absolute", right: 0, top: "52px", background: "#fff", border: "1px solid var(--ink)", padding: "20px", width: "290px", zIndex: 200, boxShadow: "8px 8px 0 var(--ink)" }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--muted)", marginBottom: "16px" }}>
                // Staff access
              </div>
              <form onSubmit={handleLogin} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="inquiry-field-input"
                  required
                  autoFocus
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="inquiry-field-input"
                  required
                />
                {error && (
                  <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px", color: "#d00", letterSpacing: "0.06em" }}>{error}</p>
                )}
                <button type="submit" className="btn" style={{ marginTop: "4px" }}>Sign in →</button>
              </form>
              <button
                onClick={() => setShowLogin(false)}
                style={{ marginTop: "12px", fontFamily: "'JetBrains Mono', monospace", fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--muted)", background: "none", border: "none", cursor: "pointer" }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
